import {
    ActionButtonType,
    UserResponse,
    ProfileComponentType,
    UserProfileStyle,
    ProfileUserResponse,
    PublicUserResponse
} from "@authory-packages/core";
import { V3IconBlueSky, V3IconCalendar, V3IconMessage, V3IconThreads, V3IconTwitter, V3IconWeb, V3IconYoutube } from "../Icons";
import { UnstyledLink } from "../ReusableStyledComponents/UnstyledLink";
import { getSocialLabel } from "../V3SocialMediaLinksManager/V3SocialMediaLinksManager.utils";
import { AuthoryColorSchemes } from "./V3ProfileHero.types";
import { ContactMeButton, ProfileSocialsWrapper, SocialSVG } from "./V3ProfileSocials.styles";
import { prepareURL } from "../../utils/prepareURL";
import { getSocialProfileUrl } from "../../utils/getSocialProfileUrl";
import { isIdentityLink } from "../../utils/profileIdentityLinks";
import { useTheme } from "styled-components";
import { CustomDarkShade } from "../ReusableStyledComponents/FontSetUtilities";

const getSocialSVG = (type: ProfileComponentType) => {
    switch (type) {
        case ProfileComponentType.ThreadsLink:
            return <V3IconThreads />;
        case ProfileComponentType.FacebookLink:
            return (
                <svg width="17" height="16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="m11.063 9.257.358-2.335h-2.24V5.407c0-.639.313-1.261 1.316-1.261h1.019V2.158S10.59 2 9.707 2c-1.845 0-3.05 1.118-3.05 3.143v1.78H4.604v2.334h2.051v5.645a8.141 8.141 0 0 0 2.525 0V9.257h1.882Z"
                        fill="#6E7687"
                    />
                </svg>
            );
        case ProfileComponentType.InstagramLink:
            return (
                <svg width="17" height="16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M11.652 1H5.38a3.868 3.868 0 0 0-3.863 3.864v6.273A3.868 3.868 0 0 0 5.379 15h6.273a3.868 3.868 0 0 0 3.864-3.863V4.864A3.868 3.868 0 0 0 11.652 1Zm2.622 10.137a2.624 2.624 0 0 1-2.622 2.62H5.38a2.624 2.624 0 0 1-2.621-2.62V4.864a2.624 2.624 0 0 1 2.621-2.622h6.273a2.624 2.624 0 0 1 2.622 2.622v6.273Z"
                        fill="#6E7687"
                    />
                    <path
                        d="M8.516 4.5c-1.93 0-3.5 1.57-3.5 3.5s1.57 3.5 3.5 3.5 3.5-1.57 3.5-3.5-1.57-3.5-3.5-3.5Zm0 5.795A2.297 2.297 0 0 1 6.22 8a2.297 2.297 0 0 1 2.295-2.295A2.297 2.297 0 0 1 10.81 8a2.297 2.297 0 0 1-2.294 2.295ZM12.29 3.4a.878.878 0 0 0-.874.875c0 .23.093.456.256.619a.881.881 0 0 0 .618.256c.231 0 .456-.094.62-.256A.879.879 0 0 0 12.29 3.4Z"
                        fill="#6E7687"
                    />
                </svg>
            );
        case ProfileComponentType.LinkedinLink:
            return (
                <svg width="17" height="16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M5.102 5.593h-2.68v8.061h2.68V5.593ZM3.782 1.703c-.917 0-1.516.602-1.516 1.393 0 .773.58 1.392 1.48 1.392h.017c.935 0 1.517-.619 1.517-1.392-.018-.79-.582-1.393-1.498-1.393ZM11.68 5.405c-1.421 0-2.058.782-2.415 1.331V5.595H6.586c.035.756 0 8.061 0 8.061h2.68V9.154c0-.24.017-.482.088-.654.193-.481.634-.98 1.374-.98.97 0 1.358.74 1.358 1.823v4.313h2.68V9.033c0-2.476-1.323-3.628-3.086-3.628Z"
                        fill="#6E7687"
                    />
                </svg>
            );
        case ProfileComponentType.TwitterLink:
            return <V3IconTwitter />;
        case ProfileComponentType.BlueskyLink:
            return <V3IconBlueSky />;
        case ProfileComponentType.PinterestLink:
            return (
                <svg width="17" height="16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g clipPath="url(#pinterest-social-link)">
                        <path
                            d="M8.516.016A8 8 0 0 0 5.6 15.468c-.07-.633-.133-1.607.028-2.298.145-.624.938-3.976.938-3.976s-.24-.48-.24-1.188c0-1.112.645-1.943 1.448-1.943.683 0 1.013.513 1.013 1.127 0 .687-.437 1.713-.663 2.664-.189.796.4 1.445 1.185 1.445 1.422 0 2.514-1.499 2.514-3.663 0-1.915-1.376-3.255-3.341-3.255-2.276 0-3.613 1.708-3.613 3.472 0 .688.265 1.425.596 1.826a.24.24 0 0 1 .055.23c-.06.252-.195.795-.222.906-.035.147-.116.178-.267.107-1-.465-1.624-1.925-1.624-3.099 0-2.523 1.833-4.84 5.286-4.84 2.775 0 4.931 1.977 4.931 4.62 0 2.757-1.738 4.976-4.15 4.976-.811 0-1.574-.422-1.834-.92 0 0-.402 1.528-.499 1.903-.18.695-.668 1.566-.994 2.097A8 8 0 1 0 8.516.016Z"
                            fill="#6E7687"
                        />
                    </g>
                    <defs>
                        <clipPath id="pinterest-social-link">
                            <path fill="#fff" d="M.516 0h16v16h-16z" />
                        </clipPath>
                    </defs>
                </svg>
            );
        case ProfileComponentType.SoundcloudLink:
            return (
                <svg width="17" height="16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g clipPath="url(#soundcloud-social-link)">
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M16.516 8a8 8 0 1 1-16 0 8 8 0 0 1 16 0ZM2.852 9.86c.024 0 .042-.018.046-.044l.1-.633-.1-.644c-.004-.027-.022-.045-.046-.045-.025 0-.043.018-.047.045l-.084.643.084.633c.004.027.023.045.047.045Zm.428.387c.025 0 .045-.02.049-.047l.13-1.018-.13-1.04c-.004-.028-.024-.047-.049-.047-.024 0-.044.02-.048.046l-.114 1.041.114 1.018c.003.028.024.047.048.047Zm.456.182c.03 0 .055-.023.058-.056l.122-1.19-.122-1.235c-.004-.033-.028-.057-.058-.057-.03 0-.056.024-.058.057L3.57 9.183l.108 1.19c.003.033.027.056.058.056Zm.459.048a.07.07 0 0 0 .068-.066l.116-1.228-.116-1.269c-.003-.038-.033-.067-.068-.067a.07.07 0 0 0-.069.067l-.102 1.27.102 1.227a.07.07 0 0 0 .069.066Zm.463.02c.04 0 .075-.033.077-.077l.11-1.237-.11-1.178a.078.078 0 0 0-.077-.076.079.079 0 0 0-.078.077l-.097 1.177.097 1.237a.079.079 0 0 0 .078.077Zm.554-.077v.001l.102-1.237-.102-1.916a.09.09 0 0 0-.088-.087.09.09 0 0 0-.088.088l-.09 1.915.09 1.237a.09.09 0 0 0 .088.087.09.09 0 0 0 .088-.088Zm.473 0 .096-1.23-.096-2.353a.1.1 0 0 0-.098-.096.1.1 0 0 0-.097.096L5.405 9.19l.085 1.23a.1.1 0 0 0 .097.097.1.1 0 0 0 .098-.097Zm.49-.02v.001l.09-1.216-.09-2.55a.109.109 0 0 0-.107-.107.11.11 0 0 0-.107.107l-.079 2.55.079 1.216c.002.06.05.106.107.106a.109.109 0 0 0 .108-.106Zm.371.11a.118.118 0 0 0 .118-.117l.081-1.209-.081-2.635a.118.118 0 0 0-.118-.117.118.118 0 0 0-.117.117l-.074 2.635.074 1.21a.118.118 0 0 0 .117.116Zm.609-.128.075-1.197-.075-2.568a.128.128 0 0 0-.128-.127.128.128 0 0 0-.127.127l-.067 2.568.067 1.198c.002.07.058.126.127.126.07 0 .125-.056.128-.127Zm.495-.008v.001l.069-1.19-.07-2.474a.137.137 0 0 0-.274 0l-.061 2.473.06 1.191a.137.137 0 0 0 .276 0Zm.498-.008.006-.116.056-1.065-.062-2.944a.148.148 0 0 0-.146-.147.148.148 0 0 0-.147.147l-.001.027-.056 2.916.057 1.183a.145.145 0 0 0 .035.092.146.146 0 0 0 .112.054.148.148 0 0 0 .146-.146Zm.34.14c.086 0 .156-.07.157-.156v.001l.07-1.165-.07-3.212a.164.164 0 0 0-.076-.134.158.158 0 0 0-.18.013.158.158 0 0 0-.058.121v.016l-.064 3.196.032.59.032.575c.001.085.072.155.157.155Zm.445.003h3.952A1.42 1.42 0 0 0 14.31 9.09a1.425 1.425 0 0 0-1.976-1.315 2.517 2.517 0 0 0-3.417-2.121c-.108.04-.136.084-.138.167v4.524a.168.168 0 0 0 .154.164Z"
                            fill="#6E7687"
                        />
                    </g>
                    <defs>
                        <clipPath id="soundcloud-social-link">
                            <path fill="#fff" d="M.516 0h16v16h-16z" />
                        </clipPath>
                    </defs>
                </svg>
            );
        case ProfileComponentType.TikTokLink:
            return (
                <svg width="17" height="16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M14.514 6.664a3.745 3.745 0 0 1-3.477-1.673v5.755A4.257 4.257 0 0 1 6.776 15a4.257 4.257 0 0 1-4.26-4.254 4.257 4.257 0 0 1 4.26-4.253c.089 0 .176.008.263.014v2.096c-.087-.01-.173-.027-.263-.027-1.2 0-2.174.972-2.174 2.171 0 1.2.973 2.17 2.174 2.17s2.262-.944 2.262-2.143L9.059 1h2.009a3.739 3.739 0 0 0 3.448 3.335v2.33"
                        fill="#6E7687"
                    />
                </svg>
            );
        case ProfileComponentType.YoutubeLink:
            return <V3IconYoutube />;
        case ProfileComponentType.PatreonLink:
            return (
                <svg width="17" height="16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M10.49 1.25c-2.779 0-5.04 2.274-5.04 5.068 0 2.786 2.261 5.053 5.04 5.053 2.772 0 5.026-2.267 5.026-5.053 0-2.794-2.254-5.068-5.025-5.068ZM1.516 14.75h2.461V1.25H1.516v13.5Z"
                        fill="#6E7687"
                    />
                </svg>
            );
        case ProfileComponentType.RedditLink:
            return (
                <svg width="17" height="16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g clipPath="url(#reddit-link)">
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M16.516 8a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-2.705.325c.029-.106.042-.216.04-.325a1.167 1.167 0 0 0-1.975-.8 5.683 5.683 0 0 0-3.08-.984l.52-2.496 1.712.36a.8.8 0 1 0 .104-.488L9.172 3.2a.25.25 0 0 0-.258.102.247.247 0 0 0-.038.09l-.592 2.776a5.709 5.709 0 0 0-3.12.984 1.168 1.168 0 1 0-1.288 1.912 2.14 2.14 0 0 0 0 .352c0 1.792 2.088 3.248 4.664 3.248 2.576 0 4.664-1.456 4.664-3.248a2.141 2.141 0 0 0 0-.352 1.166 1.166 0 0 0 .607-.74Zm-7.725 1.04a.801.801 0 1 1 1.132-1.133.801.801 0 0 1-1.132 1.133Zm4.474 1.51a.216.216 0 0 1-.066.151L10.5 11a3.074 3.074 0 0 1-1.976.616 3.09 3.09 0 0 1-1.53-.336 3.074 3.074 0 0 1-.446-.28.216.216 0 0 1 .304-.304 2.613 2.613 0 0 0 1.664.504 2.625 2.625 0 0 0 1.672-.488.262.262 0 0 1 .159-.056.216.216 0 0 1 .213.219Zm.104-1.273a.802.802 0 0 1-.316.062l.008-.032a.8.8 0 1 1 .8-.8.796.796 0 0 1-.492.77Z"
                            fill="#6E7687"
                        />
                    </g>
                    <defs>
                        <clipPath id="reddit-link">
                            <path fill="#fff" d="M.516 0h16v16h-16z" />
                        </clipPath>
                    </defs>
                </svg>
            );
        case ProfileComponentType.QuoraLink:
            return (
                <svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M13.6891 15.264L13.5231 14.0764C12.8704 14.1243 12.1601 13.8193 11.8627 12.9082C13.3946 11.8823 14.2453 10.5934 14.6918 8.97974C15.0343 7.80991 14.9479 6.0201 14.6907 4.99486C14.0118 2.29153 11.9938 0.684362 9.0164 0.511877C6.19862 0.348581 3.65648 1.87999 2.56193 4.43388C2.20133 5.27519 2.01562 6.19097 2.01562 7.32905C2.01562 10.7578 3.76465 13.1161 6.78071 13.7609C7.46888 13.901 9.22612 13.9653 9.92251 13.7456C10.6695 14.9743 11.8714 15.9718 13.6882 15.2647L13.6891 15.264ZM5.23577 9.42852C5.02298 8.48002 5.04571 6.22547 5.27757 5.29373C5.62753 3.88806 6.22075 3.00791 7.162 2.4969C7.99734 2.12678 9.03354 2.06956 9.8073 2.43013C10.6484 2.82684 11.2326 3.62817 11.5966 4.8836C11.9313 6.361 11.9859 8.08827 11.5618 9.59066C11.4143 10.1308 11.1998 10.6605 10.9254 11.1411C10.2498 10.0354 8.71375 9.55003 7.56278 9.98398L7.7243 11.1085C8.36491 10.9617 9.24087 11.5744 9.28569 12.1599C6.84511 12.5272 5.77635 11.4135 5.23633 9.42752L5.23577 9.42852Z"
                        fill="#6E7687"
                    />
                </svg>
            );
        case ProfileComponentType.TumblrLink:
            return (
                <svg width="17" height="16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M10.156 15.5c-2.125 0-3.709-1.162-3.709-3.938V7.116H4.516V4.708C6.64 4.122 7.53 2.18 7.632.5h2.206v3.818h2.575v2.799H9.838v3.871c0 1.16.552 1.562 1.43 1.562h1.248v2.95h-2.36Z"
                        fill="#6E7687"
                    />
                </svg>
            );
        case ProfileComponentType.MastodonLink:
            return (
                <svg width="17" height="16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g clipPath="url(#mastodon-social-link)">
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M11.707 12.195c2.014-.24 3.769-1.475 3.99-2.603.347-1.778.318-4.339.318-4.339 0-3.47-2.286-4.488-2.286-4.488C12.578.238 10.6.017 8.543 0h-.05C6.436.017 4.458.238 3.306.765c0 0-2.286 1.017-2.286 4.488 0 .21 0 .432-.002.662-.003.64-.007 1.35.012 2.091.083 3.394.626 6.74 3.78 7.57 1.454.383 2.703.463 3.708.408 1.824-.1 2.848-.647 2.848-.647l-.06-1.317s-1.304.41-2.767.36c-1.45-.05-2.981-.156-3.216-1.928a3.614 3.614 0 0 1-.032-.496s1.423.346 3.227.428c1.103.05 2.138-.064 3.189-.189Zm1.612-2.47V5.522c0-.859-.22-1.541-.66-2.046-.456-.505-1.053-.764-1.793-.764-.856 0-1.505.327-1.933.982l-.417.696-.417-.696c-.429-.655-1.077-.982-1.934-.982-.74 0-1.336.259-1.791.764-.442.505-.662 1.187-.662 2.046v4.203h1.674v-4.08c0-.86.363-1.296 1.09-1.296.805 0 1.208.518 1.208 1.542v2.233h1.664V5.89c0-1.024.403-1.542 1.207-1.542.727 0 1.09.437 1.09 1.297v4.079h1.674Z"
                            fill="#6E7687"
                        />
                    </g>
                    <defs>
                        <clipPath id="mastodon-social-link">
                            <path fill="#fff" d="M.516 0h16v16h-16z" />
                        </clipPath>
                    </defs>
                </svg>
            );
        case ProfileComponentType.VimeoLink:
            return (
                <svg width="17" height="16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g clipPath="url(#video-social-link)">
                        <path
                            d="M16.508 4.426c-.071 1.473-1.16 3.488-3.263 6.047-2.175 2.672-4.016 4.009-5.522 4.009-.932 0-1.722-.814-2.366-2.442L4.065 7.564c-.478-1.627-.992-2.441-1.54-2.441-.12 0-.54.238-1.257.712l-.752-.917c.79-.655 1.568-1.311 2.335-1.968 1.053-.86 1.843-1.312 2.37-1.358 1.246-.113 2.012.691 2.3 2.414.31 1.858.526 3.014.647 3.466.358 1.542.753 2.311 1.185 2.311.335 0 .838-.5 1.508-1.5S11.891 6.524 11.94 6c.095-.863-.264-1.296-1.078-1.296-.383 0-.777.083-1.184.248.787-2.434 2.289-3.617 4.507-3.55 1.644.046 2.419 1.054 2.324 3.024Z"
                            fill="#6E7687"
                        />
                    </g>
                    <defs>
                        {" "}
                        <clipPath id="video-social-link">
                            <path fill="#fff" d="M.516 0h16v16h-16z" />
                        </clipPath>
                    </defs>
                </svg>
            );
        case ProfileComponentType.Calendar:
            return <V3IconCalendar className="skip-fill" />;
        case ProfileComponentType.Phone:
            return (
                <svg className="skip-fill" width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M3.412 2h2.823l1.412 3.53-1.765 1.058a7.764 7.764 0 0 0 3.53 3.53l1.059-1.765L14 9.765v2.823A1.412 1.412 0 0 1 12.588 14 11.294 11.294 0 0 1 2 3.412 1.412 1.412 0 0 1 3.412 2Z"
                        stroke="#ABB0BA"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            );
        case ProfileComponentType.Mail:
            return <V3IconMessage className="skip-fill" />;
        case ProfileComponentType.Info:
            return (
                <svg width="16" className="skip-fill" height="16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g clipPath="url(#icon-info-social)" stroke="#ABB0BA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M8 15A7 7 0 1 0 8 1a7 7 0 0 0 0 14ZM8 5.333h.007" />
                        <path d="M7 8h1v3h1" />
                    </g>
                    <defs>
                        <clipPath id="icon-info-social">
                            <path fill="#fff" d="M0 0h16v16H0z" />
                        </clipPath>
                    </defs>
                </svg>
            );
        case ProfileComponentType.Location:
            return (
                <svg className="skip-fill" width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 9a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z" stroke="#ABB0BA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path
                        d="M12.243 11.343 9.06 14.557a1.493 1.493 0 0 1-2.12 0l-3.183-3.214a6.078 6.078 0 0 1-1.642-3.102 6.114 6.114 0 0 1 .342-3.5 6.047 6.047 0 0 1 2.21-2.72 5.959 5.959 0 0 1 6.666 0 6.046 6.046 0 0 1 2.21 2.72 6.114 6.114 0 0 1 .342 3.5 6.078 6.078 0 0 1-1.642 3.102Z"
                        stroke="#ABB0BA"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            );
        case ProfileComponentType.Home:
            return (
                <svg className="skip-fill" width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g clipPath="url(#icon-social-home)" stroke="#ABB0BA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M2.556 8H1l7-7 7 7h-1.556M3 8v5.444c0 .413.15.809.418 1.1.268.292.632.456 1.01.456h7.143c.38 0 .743-.164 1.01-.456.269-.291.419-.687.419-1.1V8" />
                        <path d="M6 15v-4.5c0-.398.14-.78.39-1.06.25-.282.59-.44.943-.44h1.334c.353 0 .692.158.942.44.25.28.391.662.391 1.06V15" />
                    </g>
                    <defs>
                        <clipPath id="icon-social-home">
                            <path fill="#fff" d="M0 0h16v16H0z" />
                        </clipPath>
                    </defs>
                </svg>
            );
        case ProfileComponentType.Video:
            return (
                <svg className="skip-fill" width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="m10 6.63 3.794-1.558a.988.988 0 0 1 .81.03c.122.062.221.147.29.25.07.101.106.216.106.332v4.632a.592.592 0 0 1-.106.333.766.766 0 0 1-.29.249.988.988 0 0 1-.81.03L10 9.369V6.631ZM8.5 3h-6C1.672 3 1 3.746 1 4.667v6.666C1 12.253 1.672 13 2.5 13h6c.828 0 1.5-.746 1.5-1.667V4.667C10 3.747 9.328 3 8.5 3Z"
                        stroke="#ABB0BA"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            );
        default:
            return <V3IconWeb className="skip-fill" />;
    }
};

interface V3ProfileSocialsProps {
    colorVariant?: AuthoryColorSchemes;
    author: UserResponse | ProfileUserResponse | PublicUserResponse;
    customJustify?: string;
    showSocialMedia?: boolean;
    profileIconLimit?: number;
    customBackgroundColor?: string;
}

export const V3ProfileSocials = ({
    colorVariant,
    author,
    customJustify,
    showSocialMedia = true,
    profileIconLimit,
    customBackgroundColor
}: V3ProfileSocialsProps) => {
    const cmps = author.profileComponents || [];
    const componentToRender = profileIconLimit ? cmps.slice(0, profileIconLimit) : cmps;

    return (
        <ProfileSocialsWrapper $customJustify={customJustify}>
            <>
                {showSocialMedia &&
                    componentToRender.map((profileComponent, index) => {
                        const href = getSocialProfileUrl(profileComponent.type, profileComponent.url || profileComponent.text || "");

                        const icon = (
                            <SocialSVG
                                $colorVariant={colorVariant}
                                $customBackgroundColor={customBackgroundColor}
                                $customDarkShade={CustomDarkShade.Grey48}
                            >
                                {getSocialSVG(profileComponent.type)}
                            </SocialSVG>
                        );

                        return (
                            <div key={index}>
                                {href ? (
                                    <UnstyledLink
                                        rel={isIdentityLink(profileComponent.type) ? "me nofollow" : "nofollow"}
                                        href={href}
                                        target="_blank"
                                        aria-label={getSocialLabel(profileComponent.type)}
                                    >
                                        {icon}
                                    </UnstyledLink>
                                ) : (
                                    icon
                                )}
                            </div>
                        );
                    })}
            </>
        </ProfileSocialsWrapper>
    );
};

interface V3ProfileContactProps {
    colorVariant?: AuthoryColorSchemes;
    author: UserResponse | ProfileUserResponse | PublicUserResponse;
    openContactModal: () => void;
    openSubscribeModal?: () => void;
    showHighlight?: boolean;
    isEmbed?: boolean;
    isPublicAuthorHeader?: boolean;
    customBackgroundColor?: string;
}

export const V3ProfileContact = ({
    author,
    showHighlight = true,
    openContactModal,
    colorVariant,
    openSubscribeModal,
    isEmbed = false,
    isPublicAuthorHeader = false,
    customBackgroundColor
}: V3ProfileContactProps) => {
    const theme = useTheme();
    const hasContactAction = author.actionButtonType === ActionButtonType.Contact;
    const hasCustomAction = author.actionButtonType === ActionButtonType.Custom;
    const hasSubscribe = author.displayEmailSubscribe;

    const isSidebar = theme.customization.profileStyle === UserProfileStyle.sidebar;

    const SubscribeButton = () => (
        <>
            {!isEmbed && hasSubscribe && !isPublicAuthorHeader && (
                <div style={{ marginRight: hasHighLightButton && !isSidebar ? 10 : 0 }}>
                    <ContactMeButton
                        as="button"
                        onClick={openSubscribeModal}
                        $colorVariant={colorVariant}
                        $skipBaseColor
                        $customBackgroundColor={customBackgroundColor}
                        $customDarkShade={CustomDarkShade.Grey48}
                    >
                        Subscribe
                    </ContactMeButton>
                </div>
            )}
        </>
    );

    const hasHighLightButton = (hasContactAction || hasCustomAction) && showHighlight;

    const HighlightButton = () => (
        <>
            {hasHighLightButton && (
                <div>
                    {hasCustomAction && (
                        <UnstyledLink href={prepareURL(author.actionButtonUrl)} target="_blank">
                            <ContactMeButton
                                as="button"
                                $colorVariant={colorVariant}
                                $skipBaseColor
                                $customBackgroundColor={customBackgroundColor}
                                $customDarkShade={CustomDarkShade.Grey48}
                                $isPrimary
                            >
                                {author.actionButtonLabel}
                            </ContactMeButton>
                        </UnstyledLink>
                    )}
                    {hasContactAction && (
                        <ContactMeButton
                            $colorVariant={colorVariant}
                            onClick={openContactModal}
                            as="button"
                            $skipBaseColor
                            $customBackgroundColor={customBackgroundColor}
                            $customDarkShade={CustomDarkShade.Grey48}
                            $isPrimary
                        >
                            {author.actionButtonLabel}
                        </ContactMeButton>
                    )}
                </div>
            )}
        </>
    );

    return (
        <ProfileSocialsWrapper>
            {isSidebar ? (
                <>
                    <HighlightButton />
                    <SubscribeButton />
                </>
            ) : (
                <>
                    <SubscribeButton />
                    <HighlightButton />
                </>
            )}
        </ProfileSocialsWrapper>
    );
};
